import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Search, AlertTriangle, Activity, TrendingUp, Shield, Clock, ArrowRight } from 'lucide-react';

const RISK_BADGE = {
  High:   'bg-red-500/15 text-red-400 border border-red-500/25',
  Medium: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/25',
  Low:    'bg-green-500/15 text-green-400 border border-green-500/25',
};

const RISK_BAR = {
  High:   'bg-red-500',
  Medium: 'bg-yellow-400',
  Low:    'bg-green-500',
};

export default function Dashboard() {
  const { token, API_URL } = useAuth();
  const [scans, setScans] = useState([]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/my-scans`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setScans(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token, API_URL]);

  const total   = scans.length;
  const high    = scans.filter(s => s.riskLevel === 'High').length;
  const today   = scans.filter(s =>
    new Date(s.date).toDateString() === new Date().toDateString()
  ).length;
  const avgRisk = total
    ? Math.round(scans.reduce((acc, s) => acc + (s.riskScore || 0), 0) / total)
    : 0;

  const stats = [
    {
      label: 'Total Scans',
      value: total,
      icon: Search,
      sub: 'all time',
      iconBg: 'bg-[#00E5FF]/10',
      iconColor: 'text-[#00E5FF]',
      border: 'border-[#00E5FF]/15',
    },
    {
      label: 'Risky Wallets',
      value: high,
      icon: AlertTriangle,
      sub: total ? `${Math.round((high / total) * 100)}% rate` : '0% rate',
      iconBg: 'bg-red-500/10',
      iconColor: 'text-red-400',
      border: 'border-red-500/15',
    },
    {
      label: "Today's Scans",
      value: today,
      icon: Activity,
      sub: 'today',
      iconBg: 'bg-[#7C3AED]/10',
      iconColor: 'text-[#7C3AED]',
      border: 'border-[#7C3AED]/15',
    },
    {
      label: 'Avg Risk Score',
      value: avgRisk,
      icon: TrendingUp,
      sub: '/ 100',
      iconBg: 'bg-yellow-500/10',
      iconColor: 'text-yellow-400',
      border: 'border-yellow-500/15',
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">

      {/* Page header */}
      <div className="mb-7">
        <h1 className="text-xl font-bold text-white">Security Overview</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Monitor your wallet scanning activity and risk analytics
        </p>
      </div>

      {/* ── Analytics cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        {stats.map(({ label, value, icon: Icon, sub, iconBg, iconColor, border }) => (
          <div
            key={label}
            className={`glass-card p-5 border ${border} hover:brightness-110 transition-all duration-200`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-2 rounded-lg ${iconBg}`}>
                <Icon size={17} className={iconColor} />
              </div>
              <span className="text-[11px] text-slate-600">{sub}</span>
            </div>
            <div className="text-2xl font-bold text-white mb-0.5 tabular-nums">{value}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      {/* ── Quick action ─────────────────────────────────────────────── */}
      <div className="glass-card p-5 mb-5 border border-[#00E5FF]/10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <div className="text-sm font-semibold text-white mb-0.5">Scan a Wallet</div>
          <div className="text-xs text-slate-500">Analyze any Ethereum address for risks and suspicious patterns</div>
        </div>
        <Link
          to="/dashboard/scanner"
          className="flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] text-black text-sm font-bold px-4 py-2.5 rounded-xl transition-all flex-shrink-0"
        >
          <Search size={14} />
          New Scan
          <ArrowRight size={13} />
        </Link>
      </div>

      {/* ── Recent scans table ───────────────────────────────────────── */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
            <Clock size={14} className="text-[#00E5FF]" />
            Recent Scans
          </h2>
          <Link
            to="/dashboard/history"
            className="text-xs text-[#00E5FF] hover:text-[#00E5FF]/70 transition-colors"
          >
            View all →
          </Link>
        </div>

        {scans.length === 0 ? (
          <div className="text-center py-14">
            <Shield size={32} className="text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No scans yet.</p>
            <Link
              to="/dashboard/scanner"
              className="mt-3 inline-block text-xs text-[#00E5FF] hover:underline"
            >
              Scan your first wallet →
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-600 text-xs border-b border-white/[0.05]">
                  <th className="text-left pb-3 pr-4 font-medium">Wallet</th>
                  <th className="text-left pb-3 pr-4 font-medium">Risk Score</th>
                  <th className="text-left pb-3 pr-4 font-medium">Level</th>
                  <th className="text-left pb-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {scans.slice(0, 8).map((scan, i) => (
                  <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 pr-4 font-mono text-xs text-slate-400">
                      {scan.wallet?.slice(0, 10)}…{scan.wallet?.slice(-6)}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="h-1 w-20 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${RISK_BAR[scan.riskLevel] || 'bg-slate-500'}`}
                            style={{ width: `${scan.riskScore}%` }}
                          />
                        </div>
                        <span className="text-slate-400 text-xs tabular-nums">{scan.riskScore}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${RISK_BADGE[scan.riskLevel] || ''}`}>
                        {scan.riskLevel}
                      </span>
                    </td>
                    <td className="py-3 text-slate-600 text-xs tabular-nums">
                      {new Date(scan.date).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
